import { createRoot } from "solid-js";
import { createStore } from "solid-js/store";
import { api, type ReleaseNotes } from "@/api/client";

/**
 * What changed in the app, and whether this person has been told yet.
 *
 * <p>A store rather than component state because two places reach it: the dashboard, which loads the
 * notes and lets them raise the dialog by themselves after an update, and the profile menu, which
 * reopens the same dialog on demand from anywhere it is rendered.</p>
 *
 * <p>The decision about what counts as new is the SERVER's — it holds the version this person last
 * acknowledged, and version order is a rule rather than a string sort. This side only reads the
 * `isNew` flags it is given, and the `announce` flags on the entries, which say what is worth
 * interrupting somebody for.</p>
 */
interface ReleaseNotesState {
  notes: ReleaseNotes | null;
  open: boolean;
  /**
   * Whether the dialog is showing the whole history or only what arrived since last time.
   *
   * <p>Two ways in, two things to show. Opening by itself after an update is an interruption, and it
   * has to earn it by being short: only the releases this reader has not met, leading with the entries
   * they announce and folding the rest. Opening it deliberately from the profile menu is a request to
   * browse, so that shows everything, unfolded.</p>
   */
  showAll: boolean;
  loaded: boolean;
}

function createReleaseNotesStore() {
  const [state, setState] = createStore<ReleaseNotesState>({
    notes: null,
    open: false,
    showAll: false,
    loaded: false,
  });

  // The in-flight load, so the dashboard mounting twice (or a reopen racing the first fetch) awaits
  // the SAME request rather than firing a second one.
  let inflight: Promise<void> | null = null;

  async function load(): Promise<void> {
    if (state.loaded) return;
    if (inflight) return inflight;

    inflight = (async () => {
      try {
        const notes = await api.releaseNotes.list();
        setState({ notes, loaded: true });
      } catch {
        // Nobody should meet an error because the app tried to tell them what it had changed. Silence
        // leaves the dashboard exactly as it was before this feature existed.
        setState({ loaded: true });
      } finally {
        inflight = null;
      }
    })();

    return inflight;
  }

  /** The releases this reader has not met, newest first. Empty on a first visit — the server stamps those. */
  const unseen = () => (state.notes?.releases ?? []).filter((r) => r.isNew);

  /**
   * Whether anything this reader has not met is worth interrupting them for.
   *
   * <p>Unseen alone is not enough. A release carries every fix and tweak that went out with it, and a
   * dialog raised over a corrected label teaches people to close it unread — so only an entry the release
   * marks `announce` earns one. Nothing is recorded while it stays shut, so the rest are still unseen
   * when something does open it, and come along folded.</p>
   */
  const hasAnnouncement = () => unseen().some((r) => r.entries.some((e) => e.announce));

  /**
   * Loads the notes and raises the dialog if something announced arrived since last time.
   *
   * <p>Called on the dashboard, which is where a signed-in person lands and where announcements
   * already live. Deliberately NOT on the configurator: a modal over a door somebody is halfway
   * through building is an interruption they did not ask for, and the profile menu is there for
   * whenever they do want it.</p>
   */
  async function announce(): Promise<void> {
    await load();
    if (hasAnnouncement()) setState({ open: true, showAll: false });
  }

  /** Opens the dialog on the full history, whether or not anything is new. */
  function openHistory(): void {
    setState({ open: true, showAll: true });
    void load();
  }

  /**
   * Closes the dialog and records that everything shown has been read.
   *
   * <p>Marks seen however it was closed — the button, the X, the backdrop. Treating only the button as
   * an acknowledgement would re-raise the same dialog on the next visit for anyone who closed it the
   * other two ways, which reads as the app being broken rather than as insistence.</p>
   *
   * <p>Nothing new means nothing to record: reopening the history from the profile menu must not write.</p>
   */
  function close(): void {
    const hadUnseen = unseen().length > 0;
    setState({ open: false });
    if (!hadUnseen) return;

    // Clear the flags locally too. The next load would say the same thing, but the profile menu can
    // reopen this before then, and it should not still be announcing news the reader just dismissed.
    setState("notes", (notes) =>
      notes ? { ...notes, releases: notes.releases.map((r) => ({ ...r, isNew: false })) } : notes,
    );

    // Fire and forget. The dialog has already closed, and a failed write costs one repeated showing
    // on the next visit — cheaper than making somebody wait to close a dialog.
    void api.releaseNotes.markSeen().catch(() => {});
  }

  return { state, load, announce, openHistory, close, unseen };
}

const store = createRoot(createReleaseNotesStore);

export const releaseNotesState = store.state;
export const announceReleaseNotes = store.announce;
export const openReleaseNotes = store.openHistory;
export const closeReleaseNotes = store.close;
export const unseenReleases = store.unseen;
